import { ref } from 'vue';
import { defineStore } from 'pinia';

/**
 * One-shot channel used to apply a filter from the offer-details modals
 * ("Применить фильтр") to the kept-alive results page. The details page sets
 * the intent before navigating back; `OfferFilters` consumes it on activation,
 * applies the matching filter and clears it so it fires only once.
 */
export type FilterIntent = {
  withoutNightTransfer?: boolean;
  withoutRecheck?: boolean;
};

export const useFilterIntentStore = defineStore('search-filter-intent', () => {
  const pending = ref<FilterIntent | null>(null);

  /**
   * Separate one-shot flag for the router's `scrollBehavior`. It cannot reuse
   * `pending` because vue-router calls `scrollBehavior` inside its own
   * `nextTick()` — i.e. AFTER the kept-alive results re-render where
   * `OfferFilters.onActivated` already consumed `pending`. This flag survives
   * that consume and is cleared by the router so the freshly filtered list is
   * forced to the top instead of restoring the pre-navigation scroll offset.
   */
  const scrollToTopOnReturn = ref(false);

  function set(intent: FilterIntent) {
    pending.value = intent;
    scrollToTopOnReturn.value = true;
  }

  function consume(): FilterIntent | null {
    const intent = pending.value;
    pending.value = null;
    return intent;
  }

  function consumeScrollToTop(): boolean {
    const value = scrollToTopOnReturn.value;
    scrollToTopOnReturn.value = false;
    return value;
  }

  return { pending, scrollToTopOnReturn, set, consume, consumeScrollToTop };
});
